// src/components/common/Breadcrumbs.js
//
// Visual breadcrumb trail that pairs with the BreadcrumbList JSON-LD
// emitted by useSEO. Google requires the visible text and the schema
// to match — same labels, same order — so pages should pass one
// `breadcrumbs` array to both this component and useSEO.
//
// Shape: [{ name: 'Compare', url: '/vs/deepdub' }, ...]
// The last item is treated as the current page (non-clickable).

import React from 'react';
import { Box, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

export default function Breadcrumbs({ items = [] }) {
  if (!items || items.length === 0) return null;

  // Always prepend Home unless it's already there.
  const trail =
    items[0]?.url === '/'
      ? items
      : [{ name: 'Home', url: 'https://dublabs.ai/' }, ...items];

  return (
    <Box
      component="nav"
      aria-label="Breadcrumb"
      sx={{
        maxWidth: 1200,
        mx: 'auto',
        px: { xs: 3, md: 6 },
        pt: { xs: 3, md: 4 },
      }}
    >
      <Box
        component="ol"
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 0.75,
          listStyle: 'none',
          p: 0,
          m: 0,
        }}
      >
        {trail.map((item, i) => {
          const isLast = i === trail.length - 1;
          // Strip origin for internal router links; keep full URL for
          // JSON-LD but render just the path.
          const href = item.url.startsWith('https://')
            ? new URL(item.url).pathname || '/'
            : item.url;
          return (
            <Box
              key={`${item.name}-${i}`}
              component="li"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
            >
              {isLast ? (
                <Typography
                  aria-current="page"
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.78rem',
                    fontWeight: 500,
                    letterSpacing: '0.05em',
                    color: '#ffffff',
                  }}
                >
                  {item.name}
                </Typography>
              ) : (
                <Typography
                  component={RouterLink}
                  to={href}
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.78rem',
                    fontWeight: 400,
                    letterSpacing: '0.05em',
                    color: 'rgba(255,255,255,0.45)',
                    textDecoration: 'none',
                    transition: 'color 0.2s ease',
                    '&:hover': { color: 'rgba(255,255,255,0.85)' },
                  }}
                >
                  {item.name}
                </Typography>
              )}
              {!isLast && (
                <Box
                  aria-hidden="true"
                  sx={{
                    color: 'rgba(255,255,255,0.25)',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.78rem',
                    lineHeight: 1,
                  }}
                >
                  /
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
